"use client";

import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import TagButton from "../common/TagButton";
import blogImage from "../../assets/Home/Blogs/blogImage.jpeg";
import blogImage2 from "../../assets/Home/Blogs/blogImage2.jpeg";
import { useScreen } from "../../context/ScreenContext";
import axios from "axios";

const Blog = () => {
  const { isMobile } = useScreen();
  const API_BASE = import.meta.env.VITE_API_BASE_URL;
  const blogsBackground = [blogImage, blogImage2];

  const [blogsData, setBlogsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const scrollContainerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxIndex, setMaxIndex] = useState(0);
  const [activePaginationIndex, setActivePaginationIndex] = useState(0);
  // Calculate cards per page once
  const cardsPerPage = isMobile ? 1 : 2;

  // Calculate the width of a single blog card plus its margin
  const CARD_WIDTH = isMobile ? 265 : 515; // Width of a single blog card
  const CARD_GAP = 24; // Gap between cards
  const VISIBLE_PORTION = 0.2; // Portion of the next card to show (1/5th)

  // Add this at the top of your component after useState declarations
  useEffect(() => {
    // This effect ensures the activePaginationIndex always reflects the current index
    const newPaginationIndex = Math.floor(currentIndex / cardsPerPage);
    if (newPaginationIndex !== activePaginationIndex) {
      console.log(
        `Updating pagination index from ${activePaginationIndex} to ${newPaginationIndex}`
      );
      setActivePaginationIndex(newPaginationIndex);
    }
  }, [currentIndex, cardsPerPage, activePaginationIndex]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API_BASE}blogs`);
        const data = response.data;
        setBlogsData(data);
        console.log("Response for blogs", data);

        // Update maxIndex based on data length
        const newMaxIndex = Math.max(0, data.length - cardsPerPage);
        setMaxIndex(newMaxIndex);

        // Reset scroll position
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = 0;
          setCurrentIndex(0);
          setActivePaginationIndex(0);
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
        setBlogsData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogs();
  }, [API_BASE, isMobile, cardsPerPage]);

  // Recalculate maxIndex when blogs data changes or screen size changes
  useEffect(() => {
    if (blogsData.length > 0) {
      const newMaxIndex = Math.max(0, blogsData.length - cardsPerPage);
      setMaxIndex(newMaxIndex);

      // Reset current index if needed when screen size changes
      if (currentIndex > newMaxIndex) {
        setCurrentIndex(0);
        setActivePaginationIndex(0);
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({
            left: 0,
            behavior: "smooth",
          });
        }
      } else {
        // Update pagination index based on current index
        setActivePaginationIndex(Math.floor(currentIndex / cardsPerPage));
      }
    }
  }, [blogsData, isMobile, currentIndex, cardsPerPage]);

  useEffect(() => {
    // Add scroll event listener
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const handleScroll = () => {
        const scrollLeft = scrollContainer.scrollLeft;
        const newIndex = Math.round(scrollLeft / (CARD_WIDTH + CARD_GAP));

        // Only update if index has changed
        if (
          newIndex !== currentIndex &&
          newIndex >= 0 &&
          newIndex <= maxIndex
        ) {
          setCurrentIndex(newIndex);
          // Update active pagination index
          setActivePaginationIndex(Math.floor(newIndex / cardsPerPage));
        }
      };

      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, [currentIndex, CARD_WIDTH, CARD_GAP, maxIndex, cardsPerPage]);

  const scrollToIndex = (index) => {
    if (!scrollContainerRef.current) return;

    const newIndex = Math.max(0, Math.min(index, maxIndex));
    const scrollPosition = newIndex * (CARD_WIDTH + CARD_GAP);

    scrollContainerRef.current.scrollTo({
      left: scrollPosition,
      behavior: "smooth",
    });

    setCurrentIndex(newIndex);

    // Force update the pagination dot immediately
    const newPaginationIndex = Math.floor(newIndex / cardsPerPage);
    console.log(
      `Setting new pagination index: ${newPaginationIndex} from card index: ${newIndex}`
    );
    setActivePaginationIndex(newPaginationIndex);
  };

  const nextSlide = () => {
    if (currentIndex < maxIndex) {
      scrollToIndex(currentIndex + 1);
    }
  };

  const prevSlide = () => {
    if (currentIndex > 0) {
      scrollToIndex(currentIndex - 1);
    }
  };

  // Calculate total pages for pagination dots
  const totalPages = Math.ceil(blogsData.length / cardsPerPage);

  // Format date function
  const formatDate = (dateString) => {
    try {
      // Handle different date formats
      let formattedDate;
      if (dateString.includes("/")) {
        // Handle MM/DD/YYYY format
        formattedDate = new Date(dateString.split("/").reverse().join("/"));
      } else {
        // Handle other formats directly
        formattedDate = new Date(dateString);
      }

      if (isNaN(formattedDate.getTime())) {
        return dateString; // Return original if parsing fails
      }

      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
      };

      return formattedDate.toLocaleDateString("en-US", options);
    } catch (e) {
      console.error("Date formatting error:", e);
      return dateString;
    }
  };

  // Custom CSS for hiding scrollbars
  const scrollbarHideStyle = {
    msOverflowStyle: "none" /* IE and Edge */,
    scrollbarWidth: "none" /* Firefox */,
  };

  // CSS for the container to hide webkit scrollbars
  const scrollbarHideClass = `
    .scrollbar-hide::-webkit-scrollbar {
      display: none;
    }
  `;

  return (
    <>
      {/* Add the custom CSS for hiding scrollbars */}
      <style jsx global>
        {scrollbarHideClass}
      </style>

      <div className="pt-[18px] md:pt-[36px] px-[20px] md:pb-[20px] sm:px-[5vw] lg:px-[120px] w-full md:w-[1440px] flex flex-col gap-[36px] items-start font-montserrat">
        {/* Blog Tag */}
        <TagButton text="Blogs" />

        {/* Header and Navigation */}
        <div className="w-full">
          <div className="flex justify-between w-full items-center">
            <div className="flex flex-col gap-[16px]">
              <h2
                className="bg-clip-text text-transparent text-[24px] md:text-[40px] font-semibold leading-[53.24px] font-montserrat inline-block w-fit"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, #000 0%, #0060C7 100%)",
                }}
              >
                Explore Technology Blogs
              </h2>
              <p className="text-[#6B7280] text-[16px] font-normal leading-[24px] hidden md:block">
                Stay ahead of the curve with curated insights on tech, AI, and
                innovation
              </p>
            </div>
            <div className="md:flex hidden gap-[16px]">
              <button
                className={`flex items-center justify-center w-12 h-12 cursor-pointer rounded-full shadow-md ${
                  currentIndex === 0 || isLoading || blogsData.length === 0
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white text-black hover:bg-gray-100 transition-colors"
                }`}
                onClick={prevSlide}
                disabled={
                  currentIndex === 0 || isLoading || blogsData.length === 0
                }
                aria-label="Previous slide"
              >
                <IoIosArrowBack className="text-xl lg:text-2xl" />
              </button>
              <button
                className={`flex items-center justify-center w-12 h-12 cursor-pointer rounded-full shadow-md ${
                  currentIndex >= maxIndex ||
                  isLoading ||
                  blogsData.length === 0
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white text-black hover:bg-gray-100 transition-colors"
                }`}
                onClick={nextSlide}
                disabled={
                  currentIndex >= maxIndex ||
                  isLoading ||
                  blogsData.length === 0
                }
                aria-label="Next slide"
              >
                <IoIosArrowForward className="text-xl lg:text-2xl" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable container with overflow */}
        <div className="w-full relative">
          {isLoading ? (
            <div className="flex justify-center items-center h-[300px]">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : blogsData.length === 0 ? (
            <div className="flex justify-center items-center h-[300px]">
              <p className="text-gray-500">No blogs available</p>
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory"
              style={{
                ...scrollbarHideStyle,
                width: `${
                  isMobile
                    ? CARD_WIDTH + CARD_GAP + CARD_WIDTH * VISIBLE_PORTION
                    : (CARD_WIDTH + CARD_GAP) * 2 + CARD_WIDTH * VISIBLE_PORTION
                }px`, // Exactly 2 full cards + 1/5th of third
                paddingRight: `${CARD_WIDTH * VISIBLE_PORTION}px`, // Add padding to show part of the next card
              }}
              onScroll={(e) => {
                // Debounce the scroll handler
                if (!scrollContainerRef.current.scrollTimeout) {
                  scrollContainerRef.current.scrollTimeout = setTimeout(() => {
                    const scrollLeft = e.target.scrollLeft;
                    const newIndex = Math.round(
                      scrollLeft / (CARD_WIDTH + CARD_GAP)
                    );

                    if (
                      newIndex !== currentIndex &&
                      newIndex >= 0 &&
                      newIndex <= maxIndex
                    ) {
                      setCurrentIndex(newIndex);
                      // Force immediate pagination update without waiting for effect
                      const newPaginationIndex = Math.floor(
                        newIndex / cardsPerPage
                      );
                      setActivePaginationIndex(newPaginationIndex);
                    }

                    scrollContainerRef.current.scrollTimeout = null;
                  }, 50);
                }
              }}
            >
              {blogsData.map((blog, index) => {
                const formattedDateString = formatDate(blog.date);

                return (
                  <Link
                    key={index}
                    to={`/blog/${blog.id}`}
                    className="flex-shrink-0 snap-start block"
                    style={{
                      width: `${CARD_WIDTH}px`,
                      marginRight:
                        index < blogsData.length - 1 ? `${CARD_GAP}px` : "0",
                    }}
                  >
                    <div className="flex flex-col gap-[18px] w-full">
                      <div className="relative h-[142px] w-[265px] md:w-[515px] md:h-[278px] rounded-lg md:rounded-[16px] overflow-hidden">
                        <div
                          className="absolute inset-0 w-full h-full bg-cover bg-center"
                          style={{
                            backgroundImage: `url(${
                              blog.image_url ||
                              blogsBackground[index % blogsBackground.length]
                            })`,
                          }}
                        ></div>
                        <div className="absolute bottom-0 bg-[#ffffff4f] backdrop-blur-sm h-[40px] md:h-[80px] px-[24px] md:pl-[24px] md:pr-[34px] flex justify-between items-center w-full">
                          <div className="flex items-center gap-[6px] md:gap-[12px]">
                            <div className="w-[16px] h-[16px] md:w-[24px] md:h-[24px] rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                              <span className="text-[6px] md:text-xs font-bold text-gray-700">
                                {blog.author ? blog.author.charAt(0) : "?"}
                              </span>
                            </div>
                            <div className="flex flex-col gap-[4px] md:gap-[8px]">
                              <p className="text-white font-montserrat md:text-[16px] text-[8px] font-semibold leading-none">
                                {blog.author}
                              </p>
                              <p className="text-white font-montserrat text-[6px] md:text-[10px] font-normal leading-none">
                                {formattedDateString} &#8226;{" "}
                                {blog.timeToRead || blog.minuteRead || "5 min"}{" "}
                                read
                              </p>
                            </div>
                          </div>
                          <div className="flex h-[12px] md:h-[23px] px-[8px] md:px-[15px] py-[4px] justify-center items-center gap-[10px] flex-shrink-0 text-[12px] rounded-[4px] border border-[#EFF4FF]/50 bg-white">
                            <span className="text-black font-montserrat text-[6px] md:text-[12px] font-normal leading-none">
                              {blog.tag}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-[14px]">
                        <h3 className="text-[#1A202C] font-montserrat text-[16px] md:text-[20px] font-semibold leading-tight h-[60px] md:h-[50px] md:underline">
                          {blog.title}
                        </h3>
                        <p
                          className="text-[#686B6E] font-montserrat text-[14px] font-normal leading-normal hidden md:block md:h-[64px] h-[64px] overflow-hidden"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: isMobile ? 2 : 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {blog.summary || blog.description}
                        </p>
                        <span
                          className="font-montserrat text-[8px] md:text-[14px] font-semibold leading-none flex items-center gap-[5px] w-fit text-t-blue-1"
                        >
                          <span>Read post</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={isMobile ? "7" : "10"}
                            height={isMobile ? "8" : "11"}
                            viewBox="0 0 10 11"
                            fill="none"
                          >
                            <path
                              d="M9.75 1.5C9.75 1.08579 9.41421 0.749999 9 0.749999L2.25 0.749999C1.83579 0.749999 1.5 1.08579 1.5 1.5C1.5 1.91421 1.83579 2.25 2.25 2.25H8.25V8.25C8.25 8.66421 8.58579 9 9 9C9.41421 9 9.75 8.66421 9.75 8.25L9.75 1.5ZM1 9.5L1.53033 10.0303L9.53033 2.03033L9 1.5L8.46967 0.969669L0.46967 8.96967L1 9.5Z"
                              fill="#0060C7"
                            />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination Dots */}
        {!isLoading && blogsData.length > 0 && (
          <div className="flex w-full justify-center gap-2 my-4">
            {Array.from({ length: totalPages }).map((_, index) => {
              console.log(
                `Rendering dot ${index}, active index: ${activePaginationIndex}`
              );
              return (
                <button
                  key={index}
                  onClick={() => {
                    console.log(`Dot clicked: ${index}`);
                    scrollToIndex(index * cardsPerPage);
                    // Force update active pagination index immediately
                    setActivePaginationIndex(index);
                  }}
                  className={`w-[6px] h-[6px] md:w-[10px] md:h-[10px] rounded-full ${
                    index === activePaginationIndex
                      ? "bg-black"
                      : "bg-[#D9D9D9]"
                  }`}
                  data-index={index}
                  aria-label={`Go to page ${index + 1}`}
                />
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default Blog;
